/**
 * useSearch — global search across projects, tasks, contacts, invoices.
 *
 * Replaces the imperative `globalSearch(query)` from
 * `apps/web/src/services/SearchService.ts`. The pure scoring helpers are
 * preserved verbatim; the difference is that data sources now go through
 * the stores / react-query hooks reactively instead of `getState()` reads
 * that would otherwise miss live updates.
 *
 * Recent searches are UI-only state persisted in `localStorage` under the
 * `recent_searches` key (no business data — same as the previous service).
 */

import { useCallback, useMemo, useState } from 'react';
import type { Task, Project, Contact, Invoice } from '../domain/models';
import { useProjectsStore } from '../stores/projectsStore';
import { useHubStore } from '../stores/hubStore';
import { useInvoices } from './useAccounting';

const RECENT_SEARCHES_KEY = 'recent_searches';
const RECENT_SEARCHES_MAX = 10;

/**
 * Hit returned by `useSearch`. Named `SearchHit` to avoid clashing with the
 * legacy `SearchHit` types still re-exported through `@business-assistant/shared`
 * (`googleSearchClient`, `legacyTypes`, `data/types`).
 */
export interface SearchHit {
  id: string;
  type: 'task' | 'project' | 'contact' | 'invoice' | 'conversation' | 'timeEntry' | 'deal' | 'customer' | 'event' | 'chat';
  /** Legacy alias for `type`, kept for back-compat with consumers that pre-date the migration. */
  resultType?: SearchHit['type'];
  title: string;
  /** Optional breadcrumb / sub-line (legacy SearchModal/legacy SearchDropdown). */
  subtitle?: string;
  description?: string;
  /** Legacy route hint used by some consumers. */
  path?: string;
  matchedFields: string[];
  score: number; // 0-1, higher is better
}

export type SearchHitType = SearchHit['type'];

// =============================================================================
// Pure scoring helpers (unchanged from SearchService)
// =============================================================================

function calculateMatchScore(query: string, fields: string[]): number {
  let score = 0;

  fields.forEach((field) => {
    if (!field) return;
    const normalizedField = field.toLowerCase();

    if (normalizedField === query) {
      score += 1.0;
    } else if (normalizedField.startsWith(query)) {
      score += 0.8;
    } else if (normalizedField.includes(query)) {
      score += 0.5;
    } else if (new RegExp(`\\b${query}`, 'i').test(field)) {
      score += 0.6;
    }
  });

  return Math.min(score, 1.0);
}

function getMatchedFields(
  query: string,
  fields: Record<string, string | undefined>
): string[] {
  const matched: string[] = [];
  Object.entries(fields).forEach(([key, value]) => {
    if (value && value.toLowerCase().includes(query)) {
      matched.push(key);
    }
  });
  return matched;
}

function searchProjects(
  query: string,
  projects: Project[]
): SearchHit[] {
  const results: SearchHit[] = [];
  projects.forEach((project) => {
    const score = calculateMatchScore(query, [
      project.title,
      project.description || '',
      project.clientName || '',
    ]);
    if (score > 0) {
      results.push({
        id: project.id,
        type: 'project',
        title: project.title,
        description: project.description,
        matchedFields: getMatchedFields(query, {
          title: project.title,
          description: project.description,
          client: project.clientName,
        }),
        score,
      });
    }
  });
  return results;
}

function searchTasks(query: string, tasks: Task[]): SearchHit[] {
  const results: SearchHit[] = [];
  tasks.forEach((task) => {
    const score = calculateMatchScore(query, [task.title, task.description || '']);
    if (score > 0) {
      results.push({
        id: task.id,
        type: 'task',
        title: task.title,
        description: task.description,
        matchedFields: getMatchedFields(query, {
          title: task.title,
          description: task.description,
        }),
        score,
      });
    }
  });
  return results;
}

function searchContacts(query: string, contacts: Contact[]): SearchHit[] {
  const results: SearchHit[] = [];
  contacts.forEach((contact) => {
    const score = calculateMatchScore(query, [
      contact.displayName,
      contact.company || '',
      contact.role || '',
      contact.handles.email || '',
    ]);
    if (score > 0) {
      results.push({
        id: contact.id,
        type: 'contact',
        title: contact.displayName,
        description: contact.company || contact.role,
        matchedFields: getMatchedFields(query, {
          name: contact.displayName,
          company: contact.company,
          email: contact.handles.email,
        }),
        score,
      });
    }
  });
  return results;
}

function searchInvoices(query: string, invoices: Invoice[]): SearchHit[] {
  const results: SearchHit[] = [];
  invoices.forEach((invoice) => {
    const score = calculateMatchScore(query, [
      invoice.number,
      invoice.counterparty,
      invoice.items.map((i) => i.description).join(' '),
    ]);
    if (score > 0) {
      results.push({
        id: invoice.id,
        type: 'invoice',
        title: `Fattura ${invoice.number}`,
        description: invoice.counterparty,
        matchedFields: getMatchedFields(query, {
          number: invoice.number,
          counterparty: invoice.counterparty,
        }),
        score,
      });
    }
  });
  return results;
}

function readRecentFromStorage(): string[] {
  try {
    const stored =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(RECENT_SEARCHES_KEY)
        : null;
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

function writeRecentToStorage(list: string[]): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(list));
    }
  } catch {
    // Ignore storage errors (private mode, quota, etc.)
  }
}

function clearRecentStorage(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    }
  } catch {
    // Ignore
  }
}

// =============================================================================
// Public hook: useSearch
// =============================================================================

/**
 * Run a global search across the live data caches. Returns an empty array
 * for queries shorter than 2 characters. Re-runs when any of the source
 * collections change.
 */
export function useSearch(query: string, maxResults: number = 20): SearchHit[] {
  // Length-based selectors trigger re-render when collections change,
  // then we read the live arrays via getState() inside useMemo.
  const projectsLen = useProjectsStore((s) => s.projects.length);
  const tasksLen = useProjectsStore((s) => s.tasks.length);
  const contactsLen = useHubStore((s) => s.contacts.length);
  const { data: invoices = [] } = useInvoices();

  return useMemo(() => {
    const trimmed = query?.trim() ?? '';
    if (trimmed.length < 2) return [];
    const normalized = trimmed.toLowerCase();
    const projectsStore = useProjectsStore.getState();
    const hubStore = useHubStore.getState();

    return [
      ...searchProjects(normalized, projectsStore.projects),
      ...searchTasks(normalized, projectsStore.tasks),
      ...searchContacts(normalized, hubStore.contacts),
      ...searchInvoices(normalized, invoices),
    ]
      .sort((a, b) => b.score - a.score)
      .slice(0, maxResults);
  }, [query, maxResults, projectsLen, tasksLen, contactsLen, invoices]);
}

// =============================================================================
// Public hook: useRecentSearches
// =============================================================================

export interface UseRecentSearchesResult {
  searches: string[];
  save: (query: string) => void;
  clear: () => void;
}

/**
 * Manage the local "recent searches" history. Backed by `localStorage` under
 * the `recent_searches` key. The returned state is shared across components
 * in the same tab via React's reconciliation; cross-tab sync is not
 * required for this UI affordance.
 */
export function useRecentSearches(limit: number = 5): UseRecentSearchesResult {
  const [searches, setSearches] = useState<string[]>(readRecentFromStorage);

  const save = useCallback((query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearches((prev) => {
      const filtered = prev.filter((s) => s !== trimmed);
      const next = [trimmed, ...filtered].slice(0, RECENT_SEARCHES_MAX);
      writeRecentToStorage(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setSearches([]);
    clearRecentStorage();
  }, []);

  return {
    searches: searches.slice(0, limit),
    save,
    clear,
  };
}
